import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Environment, EnvironmentService } from './environment.service';

/**
 * Google Analytics (GA4) mérés. (MIRA-789)
 *
 * A gtag.js-t nem az index.html-be égetjük be, hanem innen töltjük be, mert a mérési
 * azonosítót a szerver adja a /api/config válaszban (GoogleAnalyticsMeasurementId).
 * Így környezetenként állítható, és üres értékkel teljesen kikapcsolható a mérés.
 *
 * Ez a szolgáltatás szándékosan nem függ a SessionProvider-től (a SessionProvider hívja őt),
 * ezért minden felhasználói adatot paraméterként kap.
 */
@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {
  /**
   * Azok a route-ok, amelyek útvonalparamétere titok (base64 token), nem azonosító.
   * Lásd app.module.ts: pass/:id, reg/:id, ad/:id
   */
  private static readonly TOKEN_ROUTES = ['pass', 'reg', 'ad'];

  private initialized = false;

  constructor(
    private router: Router,
    private environmentService: EnvironmentService,
    @Inject(DOCUMENT) private document: Document) {}

  /**
   * Betölti a gtag.js-t és feliratkozik a router navigációkra.
   * A konfiguráció többször is betöltődik (app indulás, admin felület), ezért
   * a második és további hívások nem csinálnak semmit.
   */
  init(measurementId: string | undefined): void {
    if (this.initialized || !measurementId) {
      return;
    }

    // fejlesztői gépen nem szemeteljük a statisztikát - ugyanez a logika, mint a MonitoringService-ben
    if (this.environmentService.getEnvironment() === Environment.Development) {
      return;
    }

    const win = window as any;
    win.dataLayer = win.dataLayer || [];
    win.gtag = function () { win.dataLayer.push(arguments); };

    const script = this.document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    this.document.head.appendChild(script);

    this.initialized = true;

    this.gtag('js', new Date());

    // A gtag a page_location-t a document.location-ből veszi, ezért a maszkolt útvonalat
    // már a config előtt beállítjuk: így a gtag saját automatikus eseményei
    // (session_start, first_visit) sem viszik fel a nyers URL-t.
    this.setPageLocation(this.document.location.pathname);

    this.gtag('config', measurementId, {
      // SPA: a navigáció nem jár oldalletöltéssel, ezért a page_view-t mi küldjük (trackPageView)
      send_page_view: false,
      // így szétválasztható a mira.momentin.hu és a test.mira.momentin.hu forgalma
      environment: this.environmentService.getEnvironment()
    });

    this.router.events
      .pipe(filter(evt => evt instanceof NavigationEnd))
      .subscribe(evt => this.trackPageView((<NavigationEnd>evt).urlAfterRedirects));
  }

  /**
   * Oldalletöltés (képernyő megnyitás) küldése.
   */
  trackPageView(url: string, title?: string): void {
    if (!this.initialized) {
      return;
    }

    this.setPageLocation(url);

    this.gtag('event', 'page_view', {
      page_location: this.pageLocation(url),
      page_title: title ?? this.document.title
    });
  }

  /**
   * Egyedi esemény küldése (pl. gombnyomás).
   * A GA4-ben legfeljebb 500 különböző eseménynév tartható nyilván, ezért a gombokat
   * nem külön eseménynévvel, hanem a button_click esemény paraméterével azonosítjuk.
   */
  trackEvent(name: string, params?: { [key: string]: any }): void {
    if (!this.initialized) {
      return;
    }

    this.gtag('event', name, params);
  }

  /**
   * A bejelentkezett felhasználó beállítása, hogy a látogatottság lebontható legyen
   * felhasználó és jogosultsági szint (szerepkör) szerint.
   * A user_id az adatbázisbeli azonosító, nevet és e-mail címet a GA-ba nem küldünk.
   */
  setUser(userId: number | string | null | undefined, role: string | null | undefined): void {
    if (!this.initialized) {
      return;
    }

    this.gtag('set', { user_id: userId !== null && userId !== undefined ? userId.toString() : null });
    this.gtag('set', 'user_properties', { user_role: role ?? 'unknown' });
  }

  /**
   * Kijelentkezéskor a felhasználóhoz kötött adatok törlése.
   */
  clearUser(): void {
    if (!this.initialized) {
      return;
    }

    this.gtag('set', { user_id: null });
    this.gtag('set', 'user_properties', { user_role: null });
  }

  /**
   * A maszkolt útvonal beállítása minden ezután küldött eseményre.
   * A gtag különben a document.location-ből veszi a page_location-t, így a maszkolás
   * csak a page_view-ra élne, a gombnyomásokra és a gtag automatikus eseményeire nem.
   */
  private setPageLocation(url: string): void {
    this.gtag('set', { page_location: this.pageLocation(url) });
  }

  private pageLocation(url: string): string {
    return this.document.location.origin + this.normalizePath(url);
  }

  /**
   * Az útvonalparamétereket kiszedjük az útvonalból (/events/123 -> /events/:id), különben
   * a GA minden esemény/felhasználó aloldalát külön oldalként mutatná.
   *
   * A számokon túl a tokent hordozó route-ok paraméterét is maszkoljuk: a /pass/<token>
   * (jelszó-visszaállítás), a /reg/<token> és a /ad/<token> útvonalparamétere egy base64
   * titok, a jelszó-visszaállító tokennel pedig új jelszó állítható be - ilyen soha nem
   * kerülhet a GA-ba. A query stringet és a hash-t eldobjuk.
   */
  private normalizePath(url: string): string {
    const segments = url.split(/[?#]/)[0].split('/');

    return segments
      .map((segment, index) => {
        if (/^\d+$/.test(segment)) {
          return ':id';
        }

        const parent = index > 0 ? segments[index - 1] : '';
        return GoogleAnalyticsService.TOKEN_ROUTES.includes(parent) && segment !== '' ? ':id' : segment;
      })
      .join('/') || '/';
  }

  private gtag(...args: any[]): void {
    const gtagFn = (window as any).gtag;
    if (typeof gtagFn !== 'function') {
      return;
    }

    gtagFn(...args);
  }
}
